import React, { useCallback, useEffect, useState } from 'react';
import { DownloadOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { getVentesParVendeur } from '../../services/rapportService';
import {
  NdoePage, NdoePageHeader, NdoeActionButton, NdoeResponsiveTable,
  NdoeStats, NdoeStatCard, NdoeError, NdoeLoading, NdoeEmpty, NdoeStatusBadge,
} from '../../components/ndoe';
import { toCsv, downloadCsv } from './exportCsv';
import { money, dateTxt } from './format';
import './rapports.css';

// Rapport « Ventes par vendeur ». Il n'existait aucun classement des ventes au
// COMPTOIR : seuls les livreurs en avaient un. Depuis le profil mixte (un livreur
// autorisé à vendre), ce trou empêchait de mesurer ce qu'il vend. La colonne
// `vente.canal` rend l'attribution non ambiguë ; on affiche donc, par personne,
// son comptoir ET sa livraison côte à côte.
const PERIODES = [
  { value: 7, label: '7 jours' },
  { value: 30, label: '30 jours' },
  { value: 90, label: '90 jours' },
  { value: 365, label: '1 an' },
];

export default function VentesParVendeur() {
  const [periode, setPeriode] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);

  const charger = useCallback(async () => {
    setLoading(true); setErreur(null);
    try {
      const { data: d } = await getVentesParVendeur({ period: periode });
      setData(d);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Chargement impossible.');
    } finally { setLoading(false); }
  }, [periode]);

  useEffect(() => { charger(); }, [charger]);

  const exporter = () => {
    const rows = data?.vendeurs || [];
    if (!rows.length) { toast.info('Rien à exporter.'); return; }
    const colonnesCsv = [
      { title: 'Vendeur', value: (v) => v.nom },
      { title: 'Rôle', value: (v) => v.role || '' },
      { title: 'Profil mixte', value: (v) => (v.profil_mixte ? 'oui' : '') },
      { title: 'Ventes comptoir', value: (v) => v.comptoir.ventes },
      { title: 'Paires comptoir', value: (v) => v.comptoir.paires },
      { title: 'CA comptoir', value: (v) => v.comptoir.ca },
      { title: 'Panier moyen', value: (v) => v.comptoir.panier_moyen },
      { title: 'Clientes servies', value: (v) => v.comptoir.clientes },
      { title: 'CA livraison', value: (v) => v.livraison.ca },
      { title: 'CA total', value: (v) => v.ca_total },
    ];
    downloadCsv(`ventes-par-vendeur-${periode}j.csv`, toCsv(colonnesCsv, rows));
  };

  const colonnes = [
    {
      title: 'Vendeur',
      key: 'nom',
      render: (r) => (
        <span className="rv-nom">
          <b>{r.nom}</b>
          {r.profil_mixte
            ? <NdoeStatusBadge kind="custom" tone="gold" label="profil mixte" />
            : <small className="rv-role">{r.role || '—'}</small>}
        </span>
      ),
    },
    { title: 'Ventes comptoir', key: 'vc', align: 'right', render: (r) => r.comptoir.ventes || '—' },
    { title: 'Paires', key: 'pc', align: 'right', render: (r) => r.comptoir.paires || '—' },
    { title: 'CA comptoir', key: 'ca', align: 'right', render: (r) => <b className="rv-ca">{money(r.comptoir.ca)}</b> },
    { title: 'Panier moyen', key: 'pm', align: 'right', render: (r) => (r.comptoir.ventes ? money(r.comptoir.panier_moyen) : '—') },
    { title: 'Clientes', key: 'cl', align: 'right', render: (r) => r.comptoir.clientes || '—' },
    { title: 'CA livraison', key: 'cal', align: 'right', render: (r) => <span className="rv-liv">{money(r.livraison.ca)}</span> },
    { title: 'Dernière vente', key: 'dv', render: (r) => dateTxt(r.derniere_vente) },
  ];

  return (
    <NdoePage className="rapports-page">
      <NdoePageHeader
        eyebrow="Rapports"
        title="Ventes par vendeur"
        subtitle="Le chiffre réalisé au comptoir, personne par personne — et ce que chacun a livré à côté."
        actions={(
          <NdoeActionButton size="sm" variant="secondary" onClick={exporter}>
            <DownloadOutlined /> Exporter
          </NdoeActionButton>
        )}
      />

      <div className="rv-periodes">
        {PERIODES.map((p) => (
          <button key={p.value} type="button"
            className={`rv-chip${periode === p.value ? ' on' : ''}`}
            onClick={() => setPeriode(p.value)}>{p.label}</button>
        ))}
      </div>

      {loading ? <NdoeLoading rows={5} />
        : erreur ? <NdoeError text={erreur} onRetry={charger} />
          : (
            <>
              <NdoeStats>
                <NdoeStatCard label="CA au comptoir" value={money(data?.totaux?.ca_comptoir)} gold />
                <NdoeStatCard label="Part du comptoir" value={`${data?.totaux?.part_comptoir_pct ?? 0} %`} />
                <NdoeStatCard label="Paires vendues" value={data?.totaux?.paires_comptoir ?? 0} />
                <NdoeStatCard label="Vendeurs actifs" value={data?.totaux?.vendeurs_actifs ?? 0} />
              </NdoeStats>

              {data?.totaux?.profils_mixtes > 0 && (
                <p className="rv-note">
                  {data.totaux.profils_mixtes} livreur(s) à <b>profil mixte</b> ont vendu au comptoir sur la période.
                  Leur chiffre comptoir est compté ici et <b>exclu</b> de leurs statistiques de livraison.
                </p>
              )}

              {(data?.vendeurs || []).length === 0
                ? <NdoeEmpty icon="🧾" title="Aucune vente sur la période" text="Les ventes au comptoir et en livraison apparaîtront ici." />
                : <NdoeResponsiveTable columns={colonnes} data={data.vendeurs} rowKey="id_user" />}
            </>
          )}
    </NdoePage>
  );
}
